import classNames from 'classnames'
import * as yup from 'yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {dispatch} from 'app/store'
import {debounce} from 'lodash'
import {FC, useCallback, useEffect, useRef} from 'react'
import {useForm} from 'react-hook-form'
import {useSelector} from 'react-redux'

import {throwError1, nameField} from 'definitions/constant'
import {keydown} from 'features/Policies/Bypass/constants'
import {selectPolicies} from 'features/Policies/policiesSelectors'
import {policiesActions} from 'features/Policies/policiesSlice'
import {EnterTabText, updatePolicyField} from 'features/Policies/policyHelpers'
import {PolicyUpdateField} from 'interfaces'
import {RenderIf} from '../RenderIf'
import {PlusIcon} from '../SvgIcon'

interface InputProps {
  field_name: string
  submitValue: (value: string) => void
  value: string
  formId: string
  buttonId: string
  resetForm: boolean
  setResetForm: React.Dispatch<boolean>
  dataCy?: string
  ariaLabel?: string
  onChange?: (value: string) => void
  classes?: string
  hideText?: boolean
  iconClasses?: string
  throwError?: string
  placeholder?: string
}

export const PolicyInputForm: FC<InputProps> = ({
  field_name,
  submitValue,
  value,
  formId,
  buttonId,
  resetForm,
  setResetForm,
  dataCy,
  ariaLabel,
  onChange,
  classes = 'w-full py-2 text-xs  placeholder:text-xs',
  hideText = false,
  iconClasses = 'right-2',
  throwError = throwError1,
  placeholder = 'Begin typing...',
}) => {
  const {updatedPolicyField, policyReverted} = useSelector(selectPolicies)
  const schema = yup.object().shape({
    [field_name]: yup.string(),
  })

  const {
    formState: {errors},
    handleSubmit,
    register,
    reset,
    watch,
    getValues,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      [field_name]: '',
    },
  })
  const refForm = useRef<HTMLFormElement>(null)
  const exe_name = watch(field_name)

  const addExecutableListener = (event: KeyboardEvent) => {
    if (
      event.code === 'Enter' ||
      event.code === 'Tab' ||
      event.code === 'NumpadEnter'
    ) {
      event.preventDefault()
      document.getElementById(buttonId)?.click()
    } else if (event.code === 'Escape') {
      reset()
    } else if (event.code === 'Backspace') {
      const values = getValues(field_name)
      if (values === '') {
        reset()
      }
    }
  }
  useEffect(() => {
    const addExecutableElement = document.getElementById(formId)

    addExecutableElement?.addEventListener(keydown, event =>
      addExecutableListener(event),
    )

    return () => {
      addExecutableElement?.removeEventListener(keydown, event =>
        addExecutableListener(event),
      )
    }
  }, [])

  const checkIfClickedOutsideFuc = useCallback(
    debounce(
      (updatedPolicyFieldValue: PolicyUpdateField, fieldValue: string) => {
        updatePolicyField(
          value,
          throwError,
          fieldValue,
          true,
          updatedPolicyFieldValue,
        )
      },
      100,
    ),
    [],
  )

  const checkIfClickedOutside = (event: MouseEvent) => {
    if (
      exe_name !== '' &&
      refForm.current &&
      !refForm.current.contains(event.target as Node) &&
      !nameField.includes((event?.target as Node)?.textContent as string)
    ) {
      checkIfClickedOutsideFuc(updatedPolicyField, exe_name)
    }
  }

  useEffect(() => {
    // to determine clicked target and close the dropdown

    // adding event listener to know whenever user clicks on page
    document.addEventListener('mousedown', checkIfClickedOutside)

    // cleanup function to remove event listener on unmount
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside)
    }
  }, [exe_name])

  useEffect(() => {
    updatePolicyField(value, throwError, exe_name, false, updatedPolicyField)
    onChange && onChange(exe_name)
    setResetForm(false)
  }, [exe_name])

  useEffect(() => {
    if (policyReverted) {
      reset()
      dispatch(policiesActions.resetPolicyUpdateField())
    }
  }, [policyReverted])

  useEffect(() => {
    if (resetForm) {
      reset()
      setResetForm(false)
    }
  }, [resetForm])

  const getInputTextColor =
    updatedPolicyField[throwError] && updatedPolicyField[value]
      ? 'text-red-100'
      : 'text-gray-600'

  return (
    <>
      <form
        id={formId}
        onSubmit={handleSubmit(() => submitValue(exe_name))}
        ref={refForm}
      >
        <input
          {...register(field_name)}
          placeholder={placeholder}
          className={classNames(
            'bg-transparent karla-light focus:outline-none placeholder-gray-600 truncate',
            updatedPolicyField[throwError] && updatedPolicyField[value]
              ? 'text-red-100'
              : 'text-dope-cyan-200',
            {
              'placeholder-red-400': errors?.exe_name?.message,
            },
            classes,
          )}
          autoComplete="off"
          autoCapitalize="words"
          data-cy={dataCy}
          aria-label={ariaLabel}
        />
        <RenderIf isTrue={!hideText && !watch(field_name)}>
          <PlusIcon
            classes="-mt-6 absolute right-1 cursor-pointer"
            onClick={() => {
              ;(
                document.querySelector(`#${formId} input`) as HTMLInputElement
              )?.focus()
            }}
            datacy={`${formId}-plus-icon`}
          />
        </RenderIf>
        <EnterTabText
          spanClasses="absolute right-9 flex karla font-normal text-xs"
          condition={Boolean(watch(field_name))}
          iconClasses={classNames('mr-2 -mt-2.5 absolute', iconClasses)}
          buttonId={buttonId}
          customStyle={{
            marginTop: '-0.825rem', // required here for ui adjustment
          }}
          textColor={getInputTextColor}
          hideText={hideText}
        />
      </form>
    </>
  )
}
